/**
 * Google Translate helpers.
 *
 * Bangla is the default site language and all page copy (menus, pages, news, etc.)
 * comes from the database — this module never stores or replaces that content.
 * It only toggles Google Translate on top of the rendered DOM when the user
 * chooses English, and restores the original database text when they choose Bangla.
 */

export const TRANSLATE_READY_EVENT = 'google-translate-ready';
export const TRANSLATE_PREF_KEY = 'translatePref';

export const getUserTranslatePreference = () =>
  sessionStorage.getItem(TRANSLATE_PREF_KEY) === 'en' ? 'en' : 'bn';

export const setUserTranslatePreference = (lang) => {
  if (lang === 'en') {
    sessionStorage.setItem(TRANSLATE_PREF_KEY, 'en');
  } else {
    sessionStorage.removeItem(TRANSLATE_PREF_KEY);
  }
};

export const getActiveLanguage = () => getUserTranslatePreference();

export const clearGoogTransCookies = () => {
  const { hostname } = window.location;
  const past = 'Thu, 01 Jan 1970 00:00:00 UTC';

  document.cookie = `googtrans=;expires=${past};path=/`;
  if (hostname.includes('.')) {
    document.cookie = `googtrans=;expires=${past};path=/;domain=${hostname}`;
    document.cookie = `googtrans=;expires=${past};path=/;domain=.${hostname}`;
  }
};

export const setGoogTransCookie = (lang) => {
  if (lang !== 'en') {
    clearGoogTransCookies();
    return;
  }

  const { hostname } = window.location;
  const expires = new Date(Date.now() + 864e5).toUTCString();
  const value = '/bn/en';

  document.cookie = `googtrans=${value};expires=${expires};path=/`;
  if (hostname.includes('.')) {
    document.cookie = `googtrans=${value};expires=${expires};path=/;domain=${hostname}`;
    document.cookie = `googtrans=${value};expires=${expires};path=/;domain=.${hostname}`;
  }
};

export const getTranslateSelect = () => document.querySelector('.goog-te-combo');

/** Re-run Google Translate after database content renders into the DOM. */
export const refreshGoogleTranslation = () => {
  if (getUserTranslatePreference() !== 'en') return;

  const select = getTranslateSelect();
  if (select?.value === 'en') {
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

export const waitForTranslateSelect = (timeoutMs = 8000) =>
  new Promise((resolve, reject) => {
    const existing = getTranslateSelect();
    if (existing) {
      resolve(existing);
      return;
    }

    let settled = false;
    const finish = (select) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(select);
    };

    const observer = new MutationObserver(() => {
      const select = getTranslateSelect();
      if (select) finish(select);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      reject(new Error('Google Translate select not found'));
    }, timeoutMs);
  });

const fireSelectChange = (select) => {
  select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));

  if (typeof select.onchange === 'function') {
    select.onchange(new Event('change'));
  }
};

const setTranslatedUiState = (lang) => {
  document.body.classList.toggle('site-translated-en', lang === 'en');
};

export const resetToBanglaDefault = async () => {
  setUserTranslatePreference('bn');
  clearGoogTransCookies();
  setTranslatedUiState('bn');
  document.documentElement.lang = 'bn';
  document.documentElement.classList.remove('translated-ltr');

  try {
    const select = getTranslateSelect() || (await waitForTranslateSelect());
    select.value = '';
    select.selectedIndex = 0;
    fireSelectChange(select);
  } catch {
    // Widget not ready — database Bangla content is already shown as-is
  }
};

export const applyGoogleTranslation = async (lang) => {
  setUserTranslatePreference(lang);
  setGoogTransCookie(lang);
  setTranslatedUiState(lang);

  const select = getTranslateSelect() || (await waitForTranslateSelect());

  if (lang === 'en') {
    select.value = 'en';
    fireSelectChange(select);
    return lang;
  }

  select.value = '';
  select.selectedIndex = 0;
  fireSelectChange(select);

  await new Promise((resolve) => setTimeout(resolve, 250));

  if (document.documentElement.lang === 'en') {
    clearGoogTransCookies();
    select.value = '';
    select.selectedIndex = 0;
    fireSelectChange(select);
  }

  document.documentElement.lang = 'bn';
  setTranslatedUiState('bn');
  return lang;
};

export const waitForLanguageChange = (targetLang, timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (getActiveLanguage() === targetLang) {
      resolve(true);
      return;
    }

    let settled = false;
    const done = (success) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(success);
    };

    const observer = new MutationObserver(() => {
      const htmlLang = document.documentElement.lang;
      if (targetLang === 'en' && htmlLang === 'en') done(true);
      if (targetLang === 'bn' && htmlLang !== 'en') done(true);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang', 'class'],
      subtree: false,
    });

    const timer = setTimeout(() => {
      const htmlLang = document.documentElement.lang;
      const success =
        targetLang === 'en' ? htmlLang === 'en' : htmlLang !== 'en';
      done(success);
    }, timeoutMs);
  });

export const ensureBanglaOnLoad = () => {
  // Every page load starts in Bangla (database content). English is opt-in only.
  sessionStorage.removeItem(TRANSLATE_PREF_KEY);
  clearGoogTransCookies();
  setTranslatedUiState('bn');
  document.documentElement.lang = 'bn';
  document.documentElement.classList.remove('translated-ltr');
};
